import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const safeNavigate = (path: string) => {
    try {
      navigate(path);
    } catch (_e) {
      try {
        window.history.pushState({}, '', path);
        window.dispatchEvent(new Event('popstate'));
      } catch (_err) {
        window.location.href = path;
      }
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Check for existing session
    try {
      const sessionPromise = supabase.auth.getSession();
      // Verificar se o retorno é uma Promise válida
      if (sessionPromise && typeof sessionPromise.then === 'function') {
        sessionPromise.then(({ data: { session } }) => {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
        }).catch(() => {
          // Em caso de erro, garantir que o loading é finalizado
          setSession(null);
          setUser(null);
          setLoading(false);
        });
      } else {
        // Se não for uma Promise válida, definir estados padrão
        setSession(null);
        setUser(null);
        setLoading(false);
      }
    } catch (error) {
      // Tratamento de erro adicional
      console.error('Erro ao verificar sessão:', error);
      setSession(null);
      setUser(null);
      setLoading(false);
    }

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: firstName,
          last_name: lastName
        }
      }
    });
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (!error) {
      safeNavigate('/');
    }
    
    return { error };
  };

  const signOut = async () => {
    try {
      // Primeiro tentar fazer logout no Supabase
      await supabase.auth.signOut();
    } catch (error) {
      console.warn('Erro ao fazer logout no Supabase:', error);
    }
    
    // Forçar limpeza local da sessão independente do resultado acima
    setSession(null);
    setUser(null);
    
    // Preservar a flag de permissão do microfone
    const micPermission = localStorage.getItem("mic_permission_requested");
    
    // Limpar todos os dados de autenticação do localStorage
    localStorage.clear();
    
    if (micPermission) {
      localStorage.setItem("mic_permission_requested", micPermission);
    }
    
    // Limpar sessionStorage também
    sessionStorage.clear();
    
    // Limpar qualquer cookie de sessão
    document.cookie.split(";").forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
    });
    
    // Usar navigate do React Router em vez de window.location.href para evitar erro 404
    safeNavigate('/auth');
    
    return { error: null };
  };

  return {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut
  };
};
